import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import router from "../router";
import { onAuthStateChanged } from "@/authManager";

// Named integrations exported from @sentry/vue (re-exported from @sentry/browser)
// Using dynamic import form avoids bundling when DSN is missing for local development.
const { browserTracingIntegration, replayIntegration } = Sentry as any;

export default {
  // Vue plugin install hook
  install(app: App) {
    const dsn = import.meta.env.VITE_SENTRY_DSN;

    // Skip initialization if no DSN is provided (e.g., in local dev)
    if (!dsn) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.info(
          "[Sentry] VITE_SENTRY_DSN not set – skipping Sentry initialization"
        );
      }
      return;
    }

    // Skip on localhost — local dev and e2e tests should not report to Sentry
    if (
      location.hostname === "localhost" ||
      location.hostname === "127.0.0.1"
    ) {
      return;
    }

    Sentry.init({
      app,
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE,
      integrations: [
        // Automatic performance tracing with Vue Router awareness
        browserTracingIntegration({ router }),
        // Session Replay integration (records user interaction on error)
        replayIntegration(),
      ],
      // Adjust this value or use tracesSampler for production
      tracesSampleRate: 0.2,
      // Sample rates for Session Replay
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
    });

    onAuthStateChanged((user) => {
      if (user) {
        Sentry.setUser({ id: user.uid, email: user.email ?? undefined });
      } else {
        Sentry.setUser(null);
      }
    });
  },
};
